import { useCanEditEmployee } from '@/services/settings/useCanEditEmployee';
import { FC, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { AiOutlineUser } from 'react-icons/ai';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { NavPage } from '../../../components/page/Breadcrumbs';
import { isManagement, useAuthenticationService } from '../../../services/authentication/authentication-service';
import {
  ReadEmployee,
  useGetEmployeesByLocationsQuery,
  useGetMeQuery,
  useGetStaffByIdQuery
} from '../../../services/gql/graphql.generated';
import { useLocationService } from '../../../services/location/LocationService';
import { useSystemSettings } from '../../../services/settings/SystemSettingsProvider';
import strings from '../team.translations';
import { TeamMemberDisplay } from './TeamMemberDisplay';
import { TeamMemberProvider } from './TeamMemberService';

export const TeamMember: FC = () => {
  const { userId } = useParams<'userId'>();
  const [searchParams] = useSearchParams();
  const handleError = useErrorHandler();

  const canEditEmployee = useCanEditEmployee();
  const allowEssEditEmail = useSystemSettings(state => state.settings.allowEssEditEmail);
  const allowEssEditPhoneNumber = useSystemSettings(state => state.settings.allowEssEditPhoneNumber);

  const role = useAuthenticationService(state => state.role);
  const isManager = isManagement(role);
  const locationList = useLocationService(state => state.locationList);
  const locationIds = locationList.map(location => location.id);

  const [{ data: employeeLocationData, error: employeeLocationError }] = useGetEmployeesByLocationsQuery({
    variables: { locationIds },
    pause: !isManager || locationIds.length === 0
  });

  useEffect(() => {
    if (employeeLocationError) {
      handleError(employeeLocationError);
    }
  }, [employeeLocationError, handleError]);

  const [{ data: userData, error: userError }, getStaffByIdQuery] = useGetStaffByIdQuery({
    variables: { id: userId },
    pause: !isManager
  });

  // Safely refresh the staff data post-mount when manager viewing a team member
  useEffect(() => {
    if (isManager && userId) {
      getStaffByIdQuery({ requestPolicy: 'network-only' });
    }
  }, [userId, isManager, getStaffByIdQuery]);

  useEffect(() => {
    if (userError) {
      handleError(userError);
    }
  }, [userError, handleError]);

  // Removed hardcoded network-only policy from config
  // to avoid render-phase cache mutations, triggering it safely in an effect instead.
  const [{ data: myData }, refetchMe] = useGetMeQuery();

  useEffect(() => {
    refetchMe({ requestPolicy: 'network-only' });
  }, [refetchMe]);

  const fromTeamCalendar = searchParams.get('from') === 'team-calendar';
  const teamCalendarAnchorDate = searchParams.get('teamCalendarAnchorDate');
  const teamCalendarUrl =
    fromTeamCalendar && teamCalendarAnchorDate
      ? `/team/calendar?anchorDate=${teamCalendarAnchorDate}`
      : '/team/calendar';

  const breadcrumbName = (employee?: ReadEmployee) => {
    return (
      <>
        <AiOutlineUser className="mr-2" />
        {employee && strings.employeeName(employee.firstName, employee.lastName || '')}
      </>
    );
  };

  // Because Suspense guarantees data presence, we can safely non-null assert components
  // that are verified by the conditional 'isManager' check.
  const myEmployee = myData!.myEmployee;
  const activeEmployee = isManager ? userData!.employee : myEmployee;

  let staffList =
    employeeLocationData?.employeesByLocations?.employees?.filter(x => x.identityId !== activeEmployee?.identityId) ??
    [];

  const managerBreadcrumbs: NavPage[] = [
    {
      name: fromTeamCalendar ? strings.teamCalendarPageTitle : strings.teamPageTitle,
      to: fromTeamCalendar ? teamCalendarUrl : '/team'
    },
    {
      name: breadcrumbName(activeEmployee as ReadEmployee),
      to: '/team-member',
      pages: staffList
        .filter(staff => !staff.isDeleted && !staff.isDeactivated)
        .map(staffMember => ({
          name: breadcrumbName(staffMember as ReadEmployee),
          to: `/team-member/${staffMember.identityId}`
        }))
    }
  ];

  const myUserId = myEmployee?.identityId;
  const isOwnPage = userId === myUserId;
  const canSelfEditContactDetails = isOwnPage && (allowEssEditEmail || allowEssEditPhoneNumber);
  const canEditDetails = canEditEmployee || canSelfEditContactDetails;

  const staffBreadcrumbs: NavPage[] = [
    {
      name: strings.myProfileTitle,
      to: `/team-member/${myUserId}`
    }
  ];

  return (
    <TeamMemberProvider>
      <TeamMemberDisplay
        userData={activeEmployee as ReadEmployee}
        breadcrumbs={isManager ? managerBreadcrumbs : staffBreadcrumbs}
        editMemberCard={canEditDetails}
        editMemberPayCard={canEditEmployee}
        showShiftTable={isManager || isOwnPage}
        refetchUserData={getStaffByIdQuery}
        isMe={isOwnPage}
      />
    </TeamMemberProvider>
  );
};
